// One line of a title's versions list (E31-S6-T1), in the kit's list rhythm (mockups/kit, wp-cur row):
// the number, the state as a badge, the contentVersion, who did the last act and when, and a line for a
// render in flight or a render that failed. Version 0 is the pack's edition and is labelled so. The
// action follows the state: Review for a version in review or approved, Open for any other version,
// and Edit beside Open for a draft when the viewer is a content owner (E31-S6-T2).
import type { Principal } from "@waypoint/contracts";
import { Button, cn, Icon } from "@waypoint/surface";
import { Link } from "wouter";
import { t } from "../../i18n/t.js";
import { StatusChip, type ChipTone } from "../../ui/form.js";
import { canEdit, editPath } from "../../versions/edit.js";
import { lastActOf, renderFailed, type VersionSummary } from "../../versions/model.js";
import { formatWhen } from "./issue-format.js";
import { failureWords, trailWords } from "./script-format.js";

const STATE_TONE: Readonly<Record<VersionSummary["state"], ChipTone>> = {
  draft: "plain",
  "in-review": "open",
  approved: "done",
  published: "done",
  retired: "quiet",
  cancelled: "quiet",
};

export function VersionRow({
  version,
  principal,
}: {
  version: VersionSummary;
  principal: Principal;
}) {
  const pack = version.source === "pack";
  const last = lastActOf(version);
  const reviewable = version.state === "in-review" || version.state === "approved";
  return (
    <li
      data-version={version.number}
      className="grid gap-1.5 rounded-12 border border-wp-line bg-wp-panel/74 px-3.5 py-3 min-[621px]:grid-cols-[minmax(0,1fr)_auto] min-[621px]:items-center"
    >
      <div className="grid min-w-0 gap-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
          <h3 className="m-0 text-14 font-semibold">
            {pack ? t("versions.pack") : t("versions.number", { n: version.number })}
          </h3>
          <StatusChip tone={STATE_TONE[version.state]}>
            {t(`versions.state.${version.state}`)}
          </StatusChip>
          {version.served ? (
            <StatusChip tone="done">
              <Icon name="check" size={12} />
              {t("versions.served")}
            </StatusChip>
          ) : null}
          <span className="font-mono text-12 text-wp-text-3">
            {t("versions.contentVersion", { cv: version.contentVersion })}
          </span>
        </div>
        <p className="m-0 text-13 text-wp-text-2">
          {pack
            ? t("versions.packLine", { version: version.pack.version })
            : last === undefined
              ? null
              : t("versions.actor", {
                  act: trailWords(last, principal.userId),
                  when: formatWhen(last.at),
                })}
        </p>
        {version.rendering ? (
          <p className="m-0 flex items-center gap-1.5 text-13 text-wp-accent">
            <Icon name="loop" size={14} />
            {t("versions.rendering")}
          </p>
        ) : renderFailed(version) ? (
          <p className={cn("m-0 flex items-center gap-1.5 text-13 text-stop-soft")}>
            <Icon name="alert" size={14} />
            {version.renderFailure === undefined
              ? t("versions.failed")
              : t("versions.failedReason", { reason: failureWords(version.renderFailure.reason) })}
          </p>
        ) : null}
      </div>
      {version.id === undefined ? null : (
        <div className="flex flex-wrap gap-2 justify-self-start">
          {canEdit(version, principal) ? (
            <Button asChild compact variant="primary">
              <Link
                href={editPath(version.id)}
                aria-label={t("versions.editLabel", { n: version.number })}
              >
                {t("versions.edit")}
              </Link>
            </Button>
          ) : null}
          <Button asChild compact variant={reviewable ? "primary" : "secondary"}>
            <Link
              href={`/content/versions/${encodeURIComponent(version.id)}/review`}
              aria-label={t(reviewable ? "versions.reviewLabel" : "versions.openLabel", {
                n: version.number,
              })}
            >
              {t(reviewable ? "versions.review" : "versions.open")}
            </Link>
          </Button>
        </div>
      )}
    </li>
  );
}
